import React, { useEffect, useState } from "react";
import { useI18n } from "../../lib/i18n";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import ExportPanel from "./ExportPanel";
import { GlassCard } from "../gc/ui";
import { useTheme } from "../../lib/theme";
import { useAuth } from "../../contexts/AuthContext";
import SkinPicker from "../gc/SkinPicker";
import ProfileAvatar from "../gc/ProfileAvatar";
import {
  PROFILE_ROLES, PROFILE_ACCENTS, AVATAR_PRESETS, presetAvatarUrl,
  sanitizeHandle, resizeAvatarFile, displayHandle, roleLabel,
} from "../../lib/profile";

export default function SettingsPanel({ project, onChange }) {
  const { t, lang, setLang } = useI18n();
  const { theme, toggle } = useTheme();
  const { user, saveApiKey, updateProfile } = useAuth();
  const db = project.db_config || { type: "mongodb", uri: "", connected: false };
  const [uri, setUri] = useState(db.uri || "");
  const [type, setType] = useState(db.type || "mongodb");
  const [apiKey, setApiKey] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || "",
    handle: user?.handle || "",
    studio: user?.studio || "",
    bio: user?.bio || "",
    role: user?.role || "indie",
    accent: user?.accent || "#0ea5e9",
    avatar_id: user?.avatar_id || "hero",
  });

  useEffect(() => {
    setProfile({
      name: user?.name || "",
      handle: user?.handle || "",
      studio: user?.studio || "",
      bio: user?.bio || "",
      role: user?.role || "indie",
      accent: user?.accent || "#0ea5e9",
      avatar_id: user?.avatar_id || "hero",
    });
  }, [user?.user_id]); // eslint-disable-line

  const setP = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const handleSave = () => {
    onChange({ db_config: { ...db, type, uri, connected: false } });
    toast.success(t("ide.saved"));
  };

  const handleTest = () => {
    if (!uri.trim()) { toast.error("URI vacía"); return; }
    const ok = /^(mongodb(\+srv)?:\/\/|postgres(ql)?:\/\/|sqlite:\/\/)/i.test(uri);
    if (!ok) { toast.error("Formato no válido"); return; }
    onChange({ db_config: { ...db, type, uri, connected: true } });
    toast.success("OK — formato válido");
  };

  const saveProfile = async (extra = {}) => {
    setSavingProfile(true);
    try {
      const dropPhoto = extra.clear_picture || extra.picture || profile.dropPhoto;
      await updateProfile({
        name: profile.name.trim(),
        handle: sanitizeHandle(profile.handle),
        studio: profile.studio.trim(),
        bio: profile.bio.trim(),
        role: profile.role,
        accent: profile.accent,
        avatar_id: extra.avatar_id !== undefined ? extra.avatar_id : profile.avatar_id,
        ...(dropPhoto && !extra.picture ? { clear_picture: true } : {}),
        ...extra,
      });
      setP({ dropPhoto: false });
      toast.success("Perfil guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el perfil");
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <h2 className="font-display text-2xl font-semibold">Ajustes</h2>

      <GlassCard className="p-5 space-y-4">
        <div className="flex items-start gap-4">
          <ProfileAvatar user={{ ...user, ...profile, picture: profile.dropPhoto ? null : user?.picture }} size={72} />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold" style={{ color: "var(--gc-muted)" }}>Tu perfil de creador</div>
            <div className="font-display text-xl font-semibold mt-0.5">{profile.name || "Sin nombre"}</div>
            <div className="text-sm" style={{ color: "var(--gc-muted)" }}>
              {displayHandle({ ...user, handle: sanitizeHandle(profile.handle) })}
              {profile.studio ? ` · ${profile.studio}` : ""}
              {profile.role ? ` · ${roleLabel(profile.role)}` : ""}
            </div>
            {profile.bio && <p className="text-sm mt-1">{profile.bio}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-xs block" style={{ color: "var(--gc-muted)" }}>
            Cómo te llamas
            <Input className="gc-input mt-1" value={profile.name} maxLength={48} onChange={(e) => setP({ name: e.target.value })} placeholder="Uriel" />
          </label>
          <label className="text-xs block" style={{ color: "var(--gc-muted)" }}>
            Handle
            <Input className="gc-input mt-1" value={profile.handle} onChange={(e) => setP({ handle: sanitizeHandle(e.target.value) })} placeholder="uriel" />
          </label>
          <label className="text-xs block" style={{ color: "var(--gc-muted)" }}>
            Nombre del estudio
            <Input className="gc-input mt-1" value={profile.studio} maxLength={48} onChange={(e) => setP({ studio: e.target.value })} placeholder="Taller Pixel" />
          </label>
          <label className="text-xs block" style={{ color: "var(--gc-muted)" }}>
            Rol
            <select className="gc-input h-10 px-3 w-full mt-1" value={profile.role} onChange={(e) => setP({ role: e.target.value })}>
              {PROFILE_ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </label>
        </div>
        <label className="text-xs block" style={{ color: "var(--gc-muted)" }}>
          Bio / lema (una línea)
          <Input className="gc-input mt-1" value={profile.bio} maxLength={160} onChange={(e) => setP({ bio: e.target.value })} placeholder="Hago platformers chiquitos que se sienten bien." />
        </label>

        <div>
          <div className="text-xs font-semibold mb-2" style={{ color: "var(--gc-muted)" }}>Avatar pixel</div>
          <div className="flex flex-wrap gap-2">
            {AVATAR_PRESETS.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setP({ avatar_id: a.id, dropPhoto: true })}
                className="rounded-2xl p-1.5 border"
                style={{
                  borderColor: profile.avatar_id === a.id && !user?.picture ? profile.accent : "var(--gc-border)",
                  background: profile.avatar_id === a.id && !user?.picture ? `${profile.accent}22` : "transparent",
                }}
                title={a.label}
              >
                <img src={presetAvatarUrl(a.id)} alt={a.label} className="w-12 h-12" style={{ imageRendering: "pixelated" }} />
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <label className="gc-btn gc-btn-ghost h-9 px-3 text-xs cursor-pointer">
              Subir foto
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  try {
                    const picture = await resizeAvatarFile(file);
                    await saveProfile({ picture, avatar_id: "" });
                  } catch (err) {
                    toast.error(err.message || "No se pudo usar esa imagen");
                  }
                }}
              />
            </label>
            {user?.picture && (
              <button
                className="gc-btn gc-btn-ghost h-9 px-3 text-xs"
                onClick={() => saveProfile({ clear_picture: true, avatar_id: profile.avatar_id || "hero" })}
              >
                Quitar foto
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold mb-2" style={{ color: "var(--gc-muted)" }}>Color personal</div>
          <div className="flex flex-wrap gap-2">
            {PROFILE_ACCENTS.map((c) => (
              <button
                key={c.id}
                type="button"
                title={c.label}
                onClick={() => setP({ accent: c.id })}
                className="w-8 h-8 rounded-full border-2"
                style={{
                  background: c.id,
                  borderColor: profile.accent === c.id ? "var(--gc-text)" : "transparent",
                }}
              />
            ))}
          </div>
        </div>

        <button className="gc-btn gc-btn-primary h-9 px-4" disabled={savingProfile} onClick={() => saveProfile()}>
          {savingProfile ? "Guardando…" : "Guardar perfil"}
        </button>
        <p className="text-[11px]" style={{ color: "var(--gc-muted)" }}>
          Se ve en el saludo, el header y tus proyectos. Email: {user?.email}
        </p>
      </GlassCard>
      <GlassCard className="p-5 space-y-3">
        <div className="text-xs font-semibold" style={{ color: "var(--gc-muted)" }}>{t("settings.lang")} · Tema</div>
        <div className="flex flex-wrap gap-2">
          <button className={"gc-btn h-9 px-4 " + (lang === "es" ? "gc-btn-primary" : "gc-btn-ghost")} onClick={() => setLang("es")}>Español</button>
          <button className={"gc-btn h-9 px-4 " + (lang === "en" ? "gc-btn-primary" : "gc-btn-ghost")} onClick={() => setLang("en")}>English</button>
          <button className="gc-btn gc-btn-ghost h-9 px-4" onClick={toggle}>{theme === "light" ? "Modo oscuro" : "Modo claro"}</button>
        </div>
        <div className="text-xs font-semibold mt-3" style={{ color: "var(--gc-muted)" }}>Mundo visual de este proyecto</div>
        <SkinPicker
          value={project.config?.skin || "aurora"}
          applyTargets={project.config?.theme_apply}
          onApplyChange={(theme_apply) => onChange({ config: { ...(project.config || {}), theme_apply } })}
          bgPreset={project.config?.bg_preset}
          bgImage={project.config?.bg_image}
          bgUploads={project.config?.bg_uploads || []}
          bgSource={project.config?.bg_source}
          onBgChange={(patch) => onChange({ config: { ...(project.config || {}), ...patch } })}
          onSelect={(patch) => onChange({
            config: {
              ...(project.config || {}),
              ...patch,
              bg_image: "",
              bg_source: "preset",
              bg_preset: patch.skin,
            },
          })}
        />
      </GlassCard>

      <GlassCard className="p-5 space-y-3">
        <div className="text-xs font-semibold" style={{ color: "var(--gc-muted)" }}>Tu cuenta · API de IA</div>
        <div className="text-sm">{user?.name} · {user?.email}</div>
        <p className="text-xs" style={{ color: "var(--gc-muted)" }}>
          Cada usuario usa su propia clave de Google AI Studio. Créala en aistudio.google.com → Get API key.
          {user?.has_api_key ? " Hay una clave guardada (no se muestra)." : " Todavía no hay clave: la generación de sprites fallará."}
        </p>
        <Label className="text-xs">Google Gemini API key</Label>
        <Input
          className="gc-input"
          type="password"
          autoComplete="off"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={user?.has_api_key ? "••••••••  (pega una nueva para reemplazar)" : "AIza…"}
        />
        <button
          className="gc-btn gc-btn-primary h-9 px-4"
          disabled={savingKey || !apiKey.trim()}
          onClick={async () => {
            setSavingKey(true);
            try {
              await saveApiKey(apiKey.trim());
              setApiKey("");
              toast.success("API key guardada. Ya puedes generar sprites con tu cuota.");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "No se pudo guardar la key");
            } finally {
              setSavingKey(false);
            }
          }}
        >
          {savingKey ? "Guardando…" : "Guardar API key"}
        </button>
      </GlassCard>

      <GlassCard className="p-5 space-y-3">
        <div className="text-xs font-semibold" style={{ color: "var(--gc-muted)" }}>{t("settings.db")}</div>
        <Label className="text-xs">Tipo</Label>
        <select className="gc-input h-10 px-3 w-full" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="mongodb">MongoDB</option>
          <option value="postgres">PostgreSQL</option>
          <option value="sqlite">SQLite</option>
        </select>
        <Input className="gc-input" value={uri} onChange={(e) => setUri(e.target.value)} placeholder="mongodb://localhost:27017" />
        <div className="flex gap-2">
          <button className="gc-btn gc-btn-ghost h-9 px-4" onClick={handleTest}>{t("settings.db.test")}</button>
          <button className="gc-btn gc-btn-primary h-9 px-4" onClick={handleSave}>{t("ide.save")}</button>
        </div>
      </GlassCard>

      <GlassCard className="p-5 text-sm space-y-1" style={{ color: "var(--gc-muted)" }}>
        <div>ID: {project.project_id}</div>
        <div>2D · {project.genre} · {project.engine}</div>
      </GlassCard>

      <div className="gc-tool overflow-hidden">
        <ExportPanel project={project} />
      </div>
    </div>
  );
}
